import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty } from "lodash";
import { sidebarHide, sidebarShow } from "actions/sidebarActions";
import { Navbar } from "react-bootstrap";
import { useAuth0 } from "utils/react-auth0-wrapper";
import "components/navbar/NavBar.scss";
import NavBrand from "components/navbar/NavBrand";
import IconNavDropdown from "components/navbar/IconNavDropdown";
import SideBarToggleButton from "components/navbar/SideBarToggleButton";
import NavLoginHeaderText from "components/navbar/NavLoginHeaderText";
import type { RootState } from "types/state";

// A parent passes nothing — userState comes from redux, client from withApollo
// — so OwnProps is empty and AdminNavBarProps is just the redux-injected props.
type AdminNavBarOwnProps = Record<never, never>;

const mapStateToProps = (state: RootState) => {
  return {
    userState: get(state.user, "userState.state", ""),
    showSidebar: state.sidebar.showSidebar,
  };
};

const mapDispatchToProps = {
  sidebarShow,
  sidebarHide,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type AdminNavBarProps = PropsFromRedux & AdminNavBarOwnProps;

const AdminNavBar = ({ userState }: WithApolloClient<AdminNavBarProps>) => {
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth0();

  // Preserved quirk: the original handler is async with no await — kept as-is so
  // conversion changes no behavior.
  // eslint-disable-next-line @typescript-eslint/require-await -- async with no await is pre-existing behavior, not changed in conversion
  const logoutWithRedirect = async () => {
    window.analytics.track("Logged Out");
    // Segment's reset() is a real runtime method, but the global
    // Window["analytics"] type in types/globals.d.ts does not declare it —
    // cast at this boundary (intersection so the cast overlaps the known shape).
    (
      window.analytics as typeof window.analytics & { reset: () => void }
    ).reset();

    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  return (
    <div>
      <header id="header" className={"icon-header-admin"}>
        <Navbar>
          <div className="nav-left">
            <NavBrand />
            <Navbar.Text>
              <p className="portal">Admin Portal</p>
            </Navbar.Text>
          </div>

          <div className="nav-right">
            {!isAuthenticated && (
              // NavLoginHeaderText calls onClick with a v1-style { redirect_uri },
              // which auth0-spa-js v2 ignores (redirect_uri moved under
              // authorizationParams) — the arg is inert. Pre-existing behavior,
              // preserved via this cast rather than reshaped.
              <NavLoginHeaderText
                onClick={
                  loginWithRedirect as unknown as (args: {
                    redirect_uri: string;
                  }) => void
                }
              />
            )}

            {isAuthenticated && !isEmpty(userState) && (
              <>
                <IconNavDropdown
                  userName={user!.nickname}
                  // Preserved: logoutWithRedirect is async but onLogout expects
                  // () => void; the original fired it fire-and-forget here.
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- pre-existing fire-and-forget async handler, not changed in conversion
                  onLogout={logoutWithRedirect}
                />
                <SideBarToggleButton />
              </>
            )}
          </div>
        </Navbar>
      </header>
    </div>
  );
};

export default connector(withApollo<AdminNavBarProps>(AdminNavBar));
